// Shared helpers for task-queue presets. Every admin/logistics/sales
// preset under ./adminPresets.ts / ./logisticsPresets.ts / ./salesPresets.ts
// is built with `taskPreset()` and the base filter fragments below.

import type { ReactNode } from "react";
import type { CrudFilter, CrudSort } from "@refinedev/core";
import type { CommentableType } from "@shared/types";
import type { Transaction } from "@shared/types/transaction";
import type { AssignmentMode } from "@ds/config/queueConfig";
import type { DataColumn } from "@ds/tables/types";
import {
  buildPipelineColumns, transactionRowTone,
} from "../list/transactionPipelineColumns";

// ─── TaskPreset — consumer-internal preset shape ────────────────────────
//
// Holds the pre-v2 legacy knobs (`dataColumns`, `defaultColumns`,
// `permanentFilters`, `defaultSort`, `rowTone`, `assignmentFilter`) that
// the pipeline-mode / task-queue config files still consume. The v2
// `QueueTabConfig<Transaction>` is the OUTPUT — adapters in
// adminQueueConfig.tsx / logisticsQueueConfig.tsx / salesQueueConfig.tsx
// (`buildTxTab`/`buildTab`) project this shape onto v2's `query` + `layout`.
export interface TaskPreset {
  id: string;
  title: () => string;
  icon?: ReactNode;
  dataColumns: DataColumn<Transaction>[];
  defaultColumns: string[];
  permanentFilters: CrudFilter[];
  defaultSort: CrudSort[];
  bulkActions?: string[];
  realtimeTables?: string[];
  rowTone?: (row: Transaction) => "red" | null | undefined;
  commentableType?: CommentableType;
  assignmentFilter?: { mode: AssignmentMode };
  resourceKeyVersion?: number;
}

// ── Base filters ────────────────────────────────────────────────────────

const BASE_FILTERS: CrudFilter[] = [
  { field: "is_deleted", operator: "eq", value: false },
];

/** Active transactions — not soft-deleted, not cancelled (status_override). */
export const TASK_ACTIVE: CrudFilter[] = [
  ...BASE_FILTERS,
  // "Not cancelled, INCLUDING null status" as a SINGLE predicate
  // (`status_override IS DISTINCT FROM 'cancelled'`) rather than the old
  // `or(status<>'cancelled', status IS NULL)`.
  //
  // Why it matters: combined with the sales/logistics assignment `or`
  // (load_sales_employee_id OR unload_sales_employee_id), the old form produced
  // TWO top-level `or=` params. @refinedev/supabase has no `and` operator to
  // wrap them (it throws), and two bare top-level `or=` are NOT reliably ANDed
  // by PostgREST on the deployed build — they collapse to OR, returning the
  // whole table and computing every `*_check` column for it → statement
  // timeout. A single non-`or` predicate keeps the assignment `or` the only
  // top-level disjunction. `isdistinct` falls through @refinedev/supabase's
  // operator map to the raw PostgREST `isdistinct` operator.
  // `isdistinct` isn't in Refine's CrudOperators union (it falls through the
  // provider's operator map to raw PostgREST), so cast the whole filter.
  { field: "status_override", operator: "isdistinct", value: "cancelled" } as unknown as CrudFilter,
];

/** Minimum column set every task-queue tab shows. */
export const TASK_BASE_COLS = [
  "reference_number", "product", "load_date", "unload_date",
  "load_place", "unload_place",
];

/** Realtime tables watched by every task-queue tab. */
export const TASK_REALTIME = [
  "transactions", "transaction_transports",
  "transaction_files", "email_sends",
];

/** Helper — build a `field = "pending"` filter for a check column. */
export const pendingCheck = (field: string): CrudFilter => ({
  field, operator: "eq", value: "pending",
});

// ── Current-employee sentinel ───────────────────────────────────────────
// Task-queue presets reference the current user in permanentFilters via
// this sentinel; the queue config's `resolvePermanentFilters` (provided by
// `transactionQueueResolvers`) substitutes the actual employee id per request.

export const CURRENT_EMPLOYEE_ID = "__CURRENT_EMPLOYEE_ID__";

/** logistics-employee-is-me filter, sentinel-resolved per request. */
export const myEmployee: CrudFilter = {
  field: "logistics_employee_id", operator: "eq", value: CURRENT_EMPLOYEE_ID,
};

/** Replace the sentinel with `employeeId` wherever it appears (depth-aware). */
export function resolveCurrentEmployeeId(
  filters: CrudFilter[],
  employeeId: number | undefined,
): CrudFilter[] {
  if (!employeeId) return filters;
  const resolve = (f: CrudFilter): CrudFilter => {
    if ("field" in f && f.value === CURRENT_EMPLOYEE_ID) {
      return { ...f, value: employeeId };
    }
    if ("operator" in f && (f.operator === "or" || f.operator === "and") && Array.isArray(f.value)) {
      return { ...f, value: (f.value as CrudFilter[]).map(resolve) };
    }
    return f;
  };
  return filters.map(resolve);
}

// ── Preset factory ──────────────────────────────────────────────────────

/**
 * Build a task-queue tab config. Task queues never render the tab's
 * title/icon in-table (the tab strip owns those); the title is required by
 * `QueueTabConfig`, so callers pass it explicitly via `extra.title`.
 *
 * The shared pipeline column set (`buildPipelineColumns`) is attached so the
 * row chrome matches the pipeline pages exactly. Per-tab default visibility
 * is driven by `defaultColumns`.
 */
export function taskPreset(
  id: string,
  defaultColumns: string[],
  permanentFilters: CrudFilter[],
  extra?: Partial<TaskPreset>,
): TaskPreset {
  return {
    id,
    title: () => "",
    dataColumns: buildPipelineColumns(defaultColumns),
    defaultColumns,
    permanentFilters,
    bulkActions: [],
    realtimeTables: TASK_REALTIME,
    rowTone: transactionRowTone,
    commentableType: "transactions",
    defaultSort: [
      { field: "unload_date",      order: "desc" },
      { field: "reference_number", order: "desc" },
    ],
    ...extra,
  };
}
